// Subsidiary.tsx
//
// As lojas vêm do `decryptedInfoToken`, não de HTTP. O `mockData` "Pizzaria
// Gourmet" que morava aqui mostrava dado falso; e descomentar a chamada antiga
// (`subsidiaryService.getSubsidiaries()`) não resolveria em três níveis:
//   1. ela chama `GET /subsidiaries` (plural) e o backend registra `/subsidiary*`
//      -> 404;
//   2. o controller equivalente é um findMany SEM `include`, então voltaria sem
//      `companyName` e sem `ambients`, dos quais esta tela depende;
//   3. o mock tinha campos que o dado real não tem.
// O padrão certo já existe no repo (Items.tsx): ler a travessia do token.
import React, { useState, useMemo } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Subsidiary";
import { useCombinedStore } from "../../../../store";

interface Props {
  decryptedInfoToken: any;
  handleOpenModal: (type: string, object?: any) => void;
}

/**
 * `subsidiaryAddress` não é texto: é o payload do Google Places em base64url
 * (protobuf). Renderizar cru mostra lixo. O endereço legível é o primeiro campo
 * do protobuf (tag 0x12, comprimento no byte seguinte), então ele é recortado
 * daí. Não dando, devolve null e a tela esconde a linha em vez de mostrar sujeira.
 */
function enderecoLegivel(bruto?: string | null): string | null {
  if (!bruto) return null;
  // Já é texto legível (loja cadastrada à mão, sem passar pelo Places).
  if (/^[\p{L}\p{N}\s.,\-/º°#()]+$/u.test(bruto) && !/^[A-Za-z0-9+/_-]{40,}$/.test(bruto)) {
    return bruto;
  }
  try {
    const base64 = bruto.replace(/-/g, "+").replace(/_/g, "/");
    const binario = atob(base64 + "=".repeat((4 - (base64.length % 4)) % 4));
    const bytes = Uint8Array.from(binario, (caractere) => caractere.charCodeAt(0));
    if (bytes[0] !== 0x12) return null;
    const tamanho = bytes[1];
    const texto = new TextDecoder("utf-8").decode(bytes.slice(2, 2 + tamanho));
    return texto.trim() || null;
  } catch {
    return null;
  }
}

/** No dado real `ambients[].items` é um ARRAY (no mock antigo era um número). */
function totalDeItens(ambientes: any[] | undefined): number {
  return (ambientes ?? []).reduce(
    (total: number, ambiente: any) => total + (ambiente?.items?.length ?? 0),
    0
  );
}

const Subsidiary: React.FC<Props> = ({
  decryptedInfoToken,
  handleOpenModal,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [establishmentFilter, setEstablishmentFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  const { authUserZustand } = useCombinedStore((state) => ({
    authUserZustand: state.authUserZustand,
  }));

  // Travessia organization -> companies -> subsidiaries. O hook do token pode
  // devolver null no primeiro render, então tudo com `?.`.
  const stores: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        ...filial,
        companyName: empresa?.companyName,
        enderecoLegivel: enderecoLegivel(filial?.subsidiaryAddress),
      }))
    );
  }, [decryptedInfoToken]);

  const isLoading = !decryptedInfoToken;

  // Obter todas as lojas
  const allStores = stores;

  // Filtrar lojas
  const filteredStores = allStores.filter((store) => {
    // `subsidiaryDescription` não existe no dado real (era campo do mock), e
    // `subsidiaryAddress` cru é base64: busca pelo endereço já decodificado.
    const matchesSearch =
      store.subsidiaryName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.subsidiaryTaxPayerID?.includes(searchTerm) ||
      store.enderecoLegivel?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && store.subsidiaryStatus) ||
      (statusFilter === "inactive" && !store.subsidiaryStatus);

    const matchesEstablishment =
      establishmentFilter === "all" ||
      store.companyName === establishmentFilter;

    return matchesSearch && matchesStatus && matchesEstablishment;
  });

  const getStatusBadge = (status: boolean) => (
    <AlfredoStyles.StatusBadge $isActive={status}>
      <AlfredoStyles.MaterialIcons>
        {status ? "check_circle" : "cancel"}
      </AlfredoStyles.MaterialIcons>
      {status ? "Ativa" : "Inativa"}
    </AlfredoStyles.StatusBadge>
  );

  const getAmbientsCount = (store: any) => (
    <AlfredoStyles.AmbientBadge>
      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
        meeting_room
      </AlfredoStyles.MaterialIcons>
      {store.ambients?.length || 0} ambiente
      {store.ambients?.length !== 1 ? "s" : ""}
    </AlfredoStyles.AmbientBadge>
  );

  const getTotalItems = (store: any) => (
    <AlfredoStyles.ItemsBadge>
      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-md)" }}>
        inventory_2
      </AlfredoStyles.MaterialIcons>
      {totalDeItens(store.ambients)} itens
    </AlfredoStyles.ItemsBadge>
  );

  const getStoreLogo = (logoUrl: string) => {
    if (logoUrl && logoUrl.startsWith("http")) {
      return logoUrl;
    } else if (logoUrl && logoUrl.startsWith("data:")) {
      return logoUrl;
    } else if (logoUrl) {
      return `data:image/jpeg;base64,${logoUrl}`;
    }
    return null;
  };

  // `ambient.layout` não existe no dado real (era campo do mock): não há cor de
  // parede nem contagem de mesas para mostrar. O preview passa a dizer o que a
  // gente de fato tem — o nome do ambiente e quantos itens (PDVs) ele tem.
  const getLayoutPreview = (ambient: any) => (
    <AlfredoStyles.LayoutPreview $color="#f8f9fa">
      <AlfredoStyles.LayoutPreviewContent>
        {ambient.ambientName}
      </AlfredoStyles.LayoutPreviewContent>
      <AlfredoStyles.LayoutTablesCount>
        {ambient.items?.length ?? 0} itens
      </AlfredoStyles.LayoutTablesCount>
    </AlfredoStyles.LayoutPreview>
  );

  // Get unique company names for filter
  const uniqueCompanies = Array.from(
    new Set(allStores.map((store) => store.companyName))
  );

  return (
    <AlfredoStyles.FieldsContainer>
      {/* Cabeçalho */}
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons style={{ fontSize: "calc(var(--u) * 36)" }}>
              store
            </AlfredoStyles.MaterialIcons>
            Lojas
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Gerencie todas as lojas da sua rede e configure ambientes
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>

        <AlfredoStyles.HeaderActions>
          {/* Toggle View Mode */}
          <AlfredoStyles.ViewModeToggle>
            <AlfredoStyles.ViewModeButton
              $isActive={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                view_list
              </AlfredoStyles.MaterialIcons>
              Lista
            </AlfredoStyles.ViewModeButton>
            <AlfredoStyles.ViewModeButton
              $isActive={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
            >
              <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                grid_view
              </AlfredoStyles.MaterialIcons>
              Grid
            </AlfredoStyles.ViewModeButton>
          </AlfredoStyles.ViewModeToggle>

          <AlfredoStyles.Button onClick={() => handleOpenModal("subsidiary")}>
            <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
            Nova Loja
          </AlfredoStyles.Button>
        </AlfredoStyles.HeaderActions>
      </AlfredoStyles.Header>

      {/* Filtros e Busca */}
      <AlfredoStyles.FilterSection>
        <AlfredoStyles.SearchWrapper>
          <AlfredoStyles.SearchIcon>
            <AlfredoStyles.MaterialIcons>search</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.SearchIcon>
          <AlfredoStyles.SearchInput
            type="text"
            placeholder="Buscar por nome, CNPJ, endereço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <AlfredoStyles.ClearButton onClick={() => setSearchTerm("")}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
            </AlfredoStyles.ClearButton>
          )}
        </AlfredoStyles.SearchWrapper>

        <AlfredoStyles.FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos os status</option>
          <option value="active">Ativas</option>
          <option value="inactive">Inativas</option>
        </AlfredoStyles.FilterSelect>

        <AlfredoStyles.FilterSelect
          value={establishmentFilter}
          onChange={(e) => setEstablishmentFilter(e.target.value)}
        >
          <option value="all">Todos os estabelecimentos</option>
          {uniqueCompanies.map((company) => (
            <option key={company} value={company}>
              {company}
            </option>
          ))}
        </AlfredoStyles.FilterSelect>
      </AlfredoStyles.FilterSection>

      {/* Estatísticas */}
      <AlfredoStyles.StatsGrid>
        <AlfredoStyles.StatCard $color="#0066cc">
          <AlfredoStyles.StatIcon>🏪</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>{allStores.length}</AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>Total de Lojas</AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>

        <AlfredoStyles.StatCard $color="#28a745">
          <AlfredoStyles.StatIcon>✅</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>
            {allStores.filter((store) => store.subsidiaryStatus).length}
          </AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>Lojas Ativas</AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>

        <AlfredoStyles.StatCard $color="#ffc107">
          <AlfredoStyles.StatIcon>🚪</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>
            {allStores.reduce(
              (acc, store) => acc + (store.ambients?.length || 0),
              0
            )}
          </AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>
            Ambientes Cadastrados
          </AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>

        <AlfredoStyles.StatCard $color="#dc3545">
          <AlfredoStyles.StatIcon>📦</AlfredoStyles.StatIcon>
          <AlfredoStyles.StatValue>
            {allStores.reduce(
              (acc, store) => acc + totalDeItens(store.ambients),
              0
            )}
          </AlfredoStyles.StatValue>
          <AlfredoStyles.StatLabel>Total de Itens</AlfredoStyles.StatLabel>
        </AlfredoStyles.StatCard>
      </AlfredoStyles.StatsGrid>

      {/* Lista/Grid de Lojas */}
      {isLoading ? (
        <AlfredoStyles.LoadingContainer>
          <AlfredoStyles.LoadingSpinner />
          <div>Carregando lojas...</div>
        </AlfredoStyles.LoadingContainer>
      ) : filteredStores.length > 0 ? (
        viewMode === "list" ? (
          <AlfredoStyles.StoresList>
            {filteredStores.map((store) => (
              <AlfredoStyles.StoreCard
                key={store.subsidiaryUuid}
                onClick={() => handleOpenModal("subsidiary", store)}
              >
                <AlfredoStyles.CardHeader>
                  <AlfredoStyles.CardHeaderLeft>
                    {getStoreLogo(store.subsidiaryLogoUrl) ? (
                      <AlfredoStyles.StoreLogo
                        src={getStoreLogo(store.subsidiaryLogoUrl)!}
                        alt={store.subsidiaryName}
                      />
                    ) : (
                      <AlfredoStyles.StoreLogoPlaceholder>
                        <AlfredoStyles.MaterialIcons
                          style={{ fontSize: "var(--fs-4xl)" }}
                        >
                          store
                        </AlfredoStyles.MaterialIcons>
                      </AlfredoStyles.StoreLogoPlaceholder>
                    )}

                    <AlfredoStyles.CardInfo>
                      <AlfredoStyles.CardTitleRow>
                        <AlfredoStyles.CardTitle>
                          {store.subsidiaryName}
                        </AlfredoStyles.CardTitle>
                        {getStatusBadge(store.subsidiaryStatus)}
                        {getAmbientsCount(store)}
                        {getTotalItems(store)}
                      </AlfredoStyles.CardTitleRow>

                      {/* `subsidiaryDescription` não existe no dado real: a
                          linha só aparece se a loja de fato tiver descrição, em
                          vez de repetir "Sem descrição" em todas. */}
                      {store.subsidiaryDescription && (
                        <AlfredoStyles.CardDescription>
                          {store.subsidiaryDescription}
                        </AlfredoStyles.CardDescription>
                      )}

                      <AlfredoStyles.CardMeta>
                        <AlfredoStyles.MetaItem>
                          <AlfredoStyles.MaterialIcons
                            style={{ fontSize: "var(--fs-base)" }}
                          >
                            badge
                          </AlfredoStyles.MaterialIcons>
                          <strong>CNPJ:</strong> {store.subsidiaryTaxPayerID}
                        </AlfredoStyles.MetaItem>
                        <AlfredoStyles.MetaItem>
                          <AlfredoStyles.MaterialIcons
                            style={{ fontSize: "var(--fs-base)" }}
                          >
                            business
                          </AlfredoStyles.MaterialIcons>
                          <strong>Estabelecimento:</strong> {store.companyName}
                        </AlfredoStyles.MetaItem>
                      </AlfredoStyles.CardMeta>

                      {store.enderecoLegivel && (
                        <AlfredoStyles.CardLocation>
                          <AlfredoStyles.MaterialIcons
                            style={{ fontSize: "var(--fs-base)" }}
                          >
                            location_on
                          </AlfredoStyles.MaterialIcons>
                          {store.enderecoLegivel}
                        </AlfredoStyles.CardLocation>
                      )}

                      {store.subsidiaryVirtualAddress && (
                        <AlfredoStyles.CardVirtualAddress>
                          <AlfredoStyles.MaterialIcons
                            style={{ fontSize: "var(--fs-base)" }}
                          >
                            language
                          </AlfredoStyles.MaterialIcons>
                          {store.subsidiaryVirtualAddress}
                        </AlfredoStyles.CardVirtualAddress>
                      )}
                    </AlfredoStyles.CardInfo>
                  </AlfredoStyles.CardHeaderLeft>

                  <AlfredoStyles.CardActions>
                    <AlfredoStyles.Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal("subsidiary", store);
                      }}
                    >
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                        edit
                      </AlfredoStyles.MaterialIcons>
                      Editar
                    </AlfredoStyles.Button>
                    <AlfredoStyles.SecondaryButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal("ambient", { subsidiary: store });
                      }}
                    >
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                        meeting_room
                      </AlfredoStyles.MaterialIcons>
                      Ver Ambientes
                    </AlfredoStyles.SecondaryButton>
                  </AlfredoStyles.CardActions>
                </AlfredoStyles.CardHeader>

                {/* Ambientes da Loja */}
                {store.ambients && store.ambients.length > 0 && (
                  <AlfredoStyles.AmbientsSection>
                    <AlfredoStyles.AmbientsHeader>
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                        meeting_room
                      </AlfredoStyles.MaterialIcons>
                      <span>Ambientes:</span>
                    </AlfredoStyles.AmbientsHeader>
                    <AlfredoStyles.AmbientsList>
                      {store.ambients.map((ambient: any, index: number) => (
                        <AlfredoStyles.AmbientCard key={index}>
                          {getLayoutPreview(ambient)}
                          <AlfredoStyles.AmbientInfo>
                            <AlfredoStyles.AmbientName>
                              {ambient.ambientName}
                            </AlfredoStyles.AmbientName>
                            <AlfredoStyles.AmbientItems>
                              {ambient.items?.length ?? 0} itens
                            </AlfredoStyles.AmbientItems>
                          </AlfredoStyles.AmbientInfo>
                        </AlfredoStyles.AmbientCard>
                      ))}
                    </AlfredoStyles.AmbientsList>
                  </AlfredoStyles.AmbientsSection>
                )}
              </AlfredoStyles.StoreCard>
            ))}
          </AlfredoStyles.StoresList>
        ) : (
          // Grid View
          <AlfredoStyles.StoresGrid>
            {filteredStores.map((store) => (
              <AlfredoStyles.StoreGridCard
                key={store.subsidiaryUuid}
                onClick={() => handleOpenModal("subsidiary", store)}
              >
                <AlfredoStyles.GridCardHeader>
                  {getStoreLogo(store.subsidiaryLogoUrl) ? (
                    <AlfredoStyles.GridStoreLogo
                      src={getStoreLogo(store.subsidiaryLogoUrl)!}
                      alt={store.subsidiaryName}
                    />
                  ) : (
                    <AlfredoStyles.GridStoreLogoPlaceholder>
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-2xl)" }}>
                        store
                      </AlfredoStyles.MaterialIcons>
                    </AlfredoStyles.GridStoreLogoPlaceholder>
                  )}
                </AlfredoStyles.GridCardHeader>

                <AlfredoStyles.GridCardContent>
                  <AlfredoStyles.GridCardTitleRow>
                    <AlfredoStyles.GridCardTitle>
                      {store.subsidiaryName}
                    </AlfredoStyles.GridCardTitle>
                    {getStatusBadge(store.subsidiaryStatus)}
                  </AlfredoStyles.GridCardTitleRow>

                  {store.subsidiaryDescription && (
                    <AlfredoStyles.GridCardDescription>
                      {store.subsidiaryDescription}
                    </AlfredoStyles.GridCardDescription>
                  )}

                  <AlfredoStyles.GridCardBadges>
                    {getAmbientsCount(store)}
                    {getTotalItems(store)}
                  </AlfredoStyles.GridCardBadges>

                  <AlfredoStyles.GridCardMeta>
                    <AlfredoStyles.GridMetaItem>
                      <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                        business
                      </AlfredoStyles.MaterialIcons>
                      {store.companyName}
                    </AlfredoStyles.GridMetaItem>
                    {store.enderecoLegivel && (
                      <AlfredoStyles.GridMetaItem>
                        <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
                          location_on
                        </AlfredoStyles.MaterialIcons>
                        {store.enderecoLegivel}
                      </AlfredoStyles.GridMetaItem>
                    )}
                  </AlfredoStyles.GridCardMeta>

                  {/* Preview dos Ambientes */}
                  {store.ambients && store.ambients.length > 0 && (
                    <AlfredoStyles.GridAmbientsPreview>
                      <AlfredoStyles.GridAmbientsTitle>
                        AMBIENTES
                      </AlfredoStyles.GridAmbientsTitle>
                      <AlfredoStyles.GridAmbientsScroll>
                        {store.ambients
                          .slice(0, 3)
                          .map((ambient: any, index: number) => (
                            <AlfredoStyles.GridAmbientPreview key={index}>
                              {getLayoutPreview(ambient)}
                              <AlfredoStyles.GridAmbientName>
                                {ambient.ambientName}
                              </AlfredoStyles.GridAmbientName>
                            </AlfredoStyles.GridAmbientPreview>
                          ))}
                        {store.ambients.length > 3 && (
                          <AlfredoStyles.GridAmbientMore>
                            +{store.ambients.length - 3} mais
                          </AlfredoStyles.GridAmbientMore>
                        )}
                      </AlfredoStyles.GridAmbientsScroll>
                    </AlfredoStyles.GridAmbientsPreview>
                  )}

                  <AlfredoStyles.GridCardActions>
                    <AlfredoStyles.Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal("subsidiary", store);
                      }}
                      style={{ fontSize: "var(--fs-xs)", padding: "var(--sp-6) var(--sp-12)", flex: 1 }}
                    >
                      Editar
                    </AlfredoStyles.Button>
                    <AlfredoStyles.SecondaryButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal("ambient", { subsidiary: store });
                      }}
                      style={{ fontSize: "var(--fs-xs)", padding: "var(--sp-6) var(--sp-12)", flex: 1 }}
                    >
                      Ambientes
                    </AlfredoStyles.SecondaryButton>
                  </AlfredoStyles.GridCardActions>
                </AlfredoStyles.GridCardContent>
              </AlfredoStyles.StoreGridCard>
            ))}
          </AlfredoStyles.StoresGrid>
        )
      ) : (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🏪</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            {searchTerm ? "Nenhuma loja encontrada" : "Nenhuma loja cadastrada"}
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            {searchTerm
              ? "Tente ajustar os filtros de busca"
              : "Comece criando sua primeira loja"}
          </AlfredoStyles.EmptyStateDescription>
          {!searchTerm && (
            <AlfredoStyles.Button
              onClick={() => handleOpenModal("subsidiary")}
              style={{ marginTop: "var(--sp-16)" }}
            >
              <AlfredoStyles.MaterialIcons>add</AlfredoStyles.MaterialIcons>
              Criar Primeira Loja
            </AlfredoStyles.Button>
          )}
        </AlfredoStyles.EmptyState>
      )}

      {/* Contador de resultados */}
      {!isLoading && filteredStores.length > 0 && (
        <AlfredoStyles.ResultsCounter>
          Mostrando <strong>{filteredStores.length}</strong> de{" "}
          <strong>{allStores.length}</strong> loja
          {allStores.length !== 1 ? "s" : ""}
        </AlfredoStyles.ResultsCounter>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default Subsidiary;
